import { mutationOptions, queryOptions } from '@tanstack/react-query'
import {
  CspLoginRequest,
  CspLoginResponse,
} from 'src/queries/csp/issuers/issuers.models'
import { getCspMe, postCspLogin } from 'src/queries/csp/issuers/issuers.network'
import { QueryError } from 'src/queries/network.utils'
import {
  cspIssuersQueryKeys,
  queryCacheOptions,
} from 'src/queries/queries.constants'

// cspIssuersQueryKeys lives in queries.constants.ts: the reactQueryInterop
// reducer (server-reachable via configureStore) seeds this key, and importing
// it from here would pull issuers.network — and with it the client graph —
// into the server bundle.

export const cspIssuersQueries = {
  /**
   * The CSP user and their issuers. Call sites must gate fetching with
   * `enabled: isCspAuthenticated` (the factory has no required args, and the
   * endpoint 401s without a CSP JWT).
   *
   * Cache override (queryCacheOptions) — stated reason: Redux parity. The
   * legacy `getCspMe` thunk fetched `/csp/me` only at explicit auth
   * boundaries (SSR seed in `server.ts`, the `App.tsx` CSP-auth effect,
   * cognito CSP signin via `getCognitoSigninThenPush`) and the issuer list
   * then lived in `state.data.issuers` for the whole session — consumer
   * mounts never refetched. App QueryClient defaults (staleTime 0 +
   * refetchOnMount when sole observer) would instead refetch on every
   * `CspSignin` mount. 15m staleTime + refetchOn* false keeps the
   * session-persistent behavior; the consumer-swap slice for this family
   * adds `reactQueryInterop` entries on GET_CSP_ME_SUCCESS /
   * POST_CSP_LOGIN_SUCCESS / POST_CSP_LOGIN_ISSUER_INFO_SUCCESS that seed or
   * invalidate this key at exactly the boundaries where issuer assignments
   * can change.
   */
  me: () =>
    queryOptions({
      queryKey: cspIssuersQueryKeys.me(),
      queryFn: () => getCspMe(),
      ...queryCacheOptions(),
    }),
}

export const cspIssuersMutations = {
  /**
   * Issuer-info refresh against `POST /csp/login` (legacy
   * `postCspLoginIssuerInfo` thunk): `CspSignin` re-posts credentials to
   * repopulate the CSP issuer ids before starting emulated sessions when no
   * default issuer is known. Modeled as a mutation, not a query: it is fired
   * imperatively during the sign-in submit flow and posts credentials, so it
   * must never be auto-refetched (or keyed by its body).
   *
   * Invalidation is component-owned: the call site should invalidate
   * `cspIssuersQueryKeys.me()` (and dispatch the Redux action that stores
   * `cspIssuerIds` while `auth.reducer` still owns the `X-Issuer-Id` header
   * source) in `onSuccess`.
   */
  loginIssuerInfo: () =>
    mutationOptions<CspLoginResponse, QueryError, CspLoginRequest>({
      mutationFn: (request) => postCspLogin(request),
    }),
}
